import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import { STRING, CONTAINS } from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import { executeScroll } from "../../utils/utils";
import Shortcut from "../../framework/Shortcut";
import ChipInputSelect from "../../framework/ChipInputSelect";
import {
  getDescriptionList,
  getDosageMarkList,
  getPackagingList,
  getPotencyList,
} from "../../services/medicine-services";
import { modeCategoryOptions } from "../products/ProductsConstants";
const ModeConfigPricingReportFilter = ({
  onFilterShow,
  applyFilterOnMount = true,
  reportRef,
  applyFilterHandler: onApplyFilters = () => {},
}) => {
  const [modes, setModes] = useState([]);
  const [modeOptions, setModeOptions] = useState([]);
  const [potencies, setPotencies] = useState([]);
  const [potencyOptions, setPotencyOptions] = useState([]);
  const [dosageMarks, setDosageMarks] = useState([]);
  const [dosageMarkOptions, setDosageMarkOptions] = useState([]);
  const [packagingSelections, setPackagingSelections] = useState([]);
  const [packagingOptions, setPackagingOptions] = useState([]);
  const [modeCategories, setModeCategories] = useState([]);
  const filterModeRef = useRef();
  const filterPotencyRef = useRef();
  const filterDosageMarkRef = useRef();
  const filterPackagingRef = useRef();
  const filterModeCategoryRef = useRef();
  const [orderBy] = useState([{ fieldName: "createdTime", orderType: "desc" }]);

  const { setCurrentComponent } = useContext(GlobalContext);

  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};

      if (modes.length) {
        filters.push(
          createFilterParam(
            "description",
            modes.join("|"),
            STRING,
            CONTAINS
          )
        );
      }
      if (potencies.length) {
        filters.push(createFilterParam("potency", potencies.join("|"), STRING, CONTAINS));
      }
      if (dosageMarks.length) {
        filters.push(
          createFilterParam("dosageMark", dosageMarks.join("|"), STRING, CONTAINS)
        );
      }
      if (packagingSelections.length) {
        filters.push(
          createFilterParam("packaging", packagingSelections.join("|"), STRING, CONTAINS)
        );
      }
      if (modeCategories.length) {
        filters.push(
          createFilterParam("modeCategory", modeCategories.join("|"), STRING, CONTAINS)
        );
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;

      if (orderBy) {
        result.orderBy = orderBy;
      }

      onApplyFilters?.(result, {
        modes,
        potencies,
        dosageMarks,
        packagingSelections,
        modeCategories,
      });
      reportRef.current?.applyFilters(result);
    },
    [
      orderBy,
      reportRef,
      onApplyFilters,
      modes,
      potencies,
      dosageMarks,
      packagingSelections,
      modeCategories,
    ]
  );

  const filterListener = useCallback(
    (event) => {
      const key = event.key.toLowerCase();
      const ctrl = event.ctrlKey;
      if (ctrl && key === "enter") {
        applyFilterHandler(true);
        return;
      }
    },
    [applyFilterHandler]
  );
  const fetchModeOptions = useCallback(async () => {
    try {
      const response = await getDescriptionList();
      if (response?.status === "success") {
        setModeOptions(response?.result?.rows || []);
      } else {
        setModeOptions([]);
      }
    } catch (error) {
      setModeOptions([]);
    }
  }, []);

  const fetchPotencyOptions = useCallback(async () => {
    try {
      const response = await getPotencyList();
      if (response?.status === "success") {
        setPotencyOptions(response?.result?.rows || []);
      } else {
        setPotencyOptions([]);
      }
    } catch (error) {
      setPotencyOptions([]);
    }
  }, []);

  const fetchDosageMarkOptions = useCallback(async () => {
    try {
      const response = await getDosageMarkList();
      if (response?.status === "success") {
        setDosageMarkOptions(response?.result?.rows || []);
      } else {
        setDosageMarkOptions([]);
      }
    } catch (error) {
      setDosageMarkOptions([]);
    }
  }, []);

  const fetchPackagingOptions = useCallback(async () => {
    try {
      const response = await getPackagingList();
      if (response?.status === "success") {
        setPackagingOptions(response?.result?.rows || []);
      } else {
        setPackagingOptions([]);
      }
    } catch (error) {
      setPackagingOptions([]);
    }
  }, []);

  useEffect(() => {
    fetchModeOptions();
    fetchPotencyOptions();
    fetchDosageMarkOptions();
    fetchPackagingOptions();
  }, [
    fetchModeOptions,
    fetchPotencyOptions,
    fetchDosageMarkOptions,
    fetchPackagingOptions,
  ]);

  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <ChipInputSelect
            id="dcpr_filter_mode"
            label="Mode"
            ref={filterModeRef}
            value={modes}
            options={modeOptions}
            isOptionEqualToValue={(option, value) => {
              const getLabel = (item) => {
                if (!item) return "";
                if (typeof item === "string") return item;
                return item?.label || item?.name || item?.description || "";
              };
              return getLabel(option) === getLabel(value);
            }}
            getOptionLabel={(option) => {
              if (!option) return "";
              if (typeof option === "string") return option;
              return option?.label || option?.name || option?.description || "";
            }}
            filterSelectedOptions={false}
            blurOnSelect={false}
            disableCloseOnSelect
            onChange={(e, newValue) => setModes(newValue || [])}
            onFocus={() => {
              setCurrentComponent("dcpr_filter_mode");
            }}
            markers={JSON.stringify({
              up: "dcpr_filter_mode",
              down: "dcpr_filter_potency",
              left: "dcpr_filter_mode",
              right: "dcpr_filter_potency",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <ChipInputSelect
            id="dcpr_filter_potency"
            label="Potency"
            ref={filterPotencyRef}
            value={potencies}
            options={potencyOptions}
            isOptionEqualToValue={(option, value) => {
              const getLabel = (item) => {
                if (!item) return "";
                if (typeof item === "string") return item;
                return item?.label || item?.name || item?.potency || "";
              };
              return getLabel(option) === getLabel(value);
            }}
            getOptionLabel={(option) => {
              if (!option) return "";
              if (typeof option === "string") return option;
              return option?.label || option?.name || option?.potency || "";
            }}
            filterSelectedOptions={false}
            blurOnSelect={false}
            disableCloseOnSelect
            onChange={(e, newValue) => setPotencies(newValue || [])}
            onFocus={() => {
              executeScroll(filterPotencyRef);
              setCurrentComponent("dcpr_filter_potency");
            }}
            markers={JSON.stringify({
              up: "dcpr_filter_mode",
              down: "dcpr_filter_dosage_mark",
              left: "dcpr_filter_mode",
              right: "dcpr_filter_dosage_mark",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <ChipInputSelect
            id="dcpr_filter_dosage_mark"
            label="Dosage Mark"
            ref={filterDosageMarkRef}
            value={dosageMarks}
            options={dosageMarkOptions}
            isOptionEqualToValue={(option, value) => {
              const getLabel = (item) => {
                if (!item) return "";
                if (typeof item === "string") return item;
                return item?.label || item?.name || item?.dosageMark || "";
              };
              return getLabel(option) === getLabel(value);
            }}
            getOptionLabel={(option) => {
              if (!option) return "";
              if (typeof option === "string") return option;
              return option?.label || option?.name || option?.dosageMark || "";
            }}
            filterSelectedOptions={false}
            blurOnSelect={false}
            disableCloseOnSelect
            onChange={(e, newValue) => setDosageMarks(newValue || [])}
            onFocus={() => {
              executeScroll(filterDosageMarkRef);
              setCurrentComponent("dcpr_filter_dosage_mark");
            }}
            markers={JSON.stringify({
              up: "dcpr_filter_potency",
              down: "dcpr_filter_packaging",
              left: "dcpr_filter_potency",
              right: "dcpr_filter_packaging",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <ChipInputSelect
            id="dcpr_filter_packaging"
            label="Packaging"
            ref={filterPackagingRef}
            value={packagingSelections}
            options={packagingOptions}
            isOptionEqualToValue={(option, value) => {
              const getLabel = (item) => {
                if (!item) return "";
                if (typeof item === "string") return item;
                return item?.label || item?.name || item?.packaging || "";
              };
              return getLabel(option) === getLabel(value);
            }}
            getOptionLabel={(option) => {
              if (!option) return "";
              if (typeof option === "string") return option;
              return option?.label || option?.name || option?.packaging || "";
            }}
            filterSelectedOptions={false}
            blurOnSelect={false}
            disableCloseOnSelect
            onChange={(e, newValue) => setPackagingSelections(newValue || [])}
            onFocus={() => {
              executeScroll(filterPackagingRef);
              setCurrentComponent("dcpr_filter_packaging");
            }}
            markers={JSON.stringify({
              up: "dcpr_filter_dosage_mark",
              down: "dcpr_filter_mode_category",
              left: "dcpr_filter_dosage_mark",
              right: "dcpr_filter_mode_category",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <ChipInputSelect
            id="dcpr_filter_mode_category"
            label="Mode Category"
            ref={filterModeCategoryRef}
            value={modeCategories}
            options={modeCategoryOptions}
            isOptionEqualToValue={(option, value) => {
              const getLabel = (item) => {
                if (!item) return "";
                if (typeof item === "string") return item;
                return item?.label || item?.name || item?.value || "";
              };
              return getLabel(option) === getLabel(value);
            }}
            getOptionLabel={(option) => {
              if (!option) return "";
              if (typeof option === "string") return option;
              return option?.label || option?.name || option?.value || "";
            }}
            filterSelectedOptions={false}
            blurOnSelect={false}
            disableCloseOnSelect
            onChange={(e, newValue) => setModeCategories(newValue || [])}
            onFocus={() => {
              executeScroll(filterModeCategoryRef);
              setCurrentComponent("dcpr_filter_mode_category");
            }}
            markers={JSON.stringify({
              up: "dcpr_filter_packaging",
              down: "dcpr_apply_filter_button",
              left: "dcpr_filter_packaging",
              right: "dcpr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="dcpr_apply_filter_button"
            label="Apply"
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => applyFilterHandler(true)}
            markers={JSON.stringify({
              up: "dcpr_filter_mode_category",
              left: "dcpr_filter_mode_category",
              down: "dcpr_apply_filter_button",
              right: "dcpr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  useEffect(() => {
    if (applyFilterOnMount) applyFilterHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilterOnMount]);

  useEffect(() => {
    if (onFilterShow) {
      filterModeRef?.current?.focus({ preventScroll: true });
    }
  }, [onFilterShow]);

  useEffect(() => {
    if (onFilterShow) {
      document.addEventListener("keyup", filterListener);
    }
    return () => {
      document.removeEventListener("keyup", filterListener);
    };
  }, [filterListener, onFilterShow]);

  return (
    <Filters
      title="Mode Config Pricing Report Filters"
      filterContent={filterContent}
      filterFooter={filterFooter}
    />
  );
};

export default ModeConfigPricingReportFilter;
